import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import axios from 'axios';
import './VideoCallRoom.css';

// Constants
const MAX_RECONNECT_ATTEMPTS = 5;
const BASE_RECONNECT_DELAY = 1000;
const PING_INTERVAL = 25000;
const ICE_GATHERING_TIMEOUT = 5000;

const rtcConfig = {
  iceServers: [
    {
      urls: [
        'stun:stun.l.google.com:19302',
        'stun:stun1.l.google.com:19302',
        'stun:stun2.l.google.com:19302',
        'stun:stun.services.mozilla.com',
      ],
    },
    {
      urls: 'turn:openrelay.metered.ca:80', // Public TURN server for testing
      username: 'openrelay.project',
      credential: 'openrelayproject',
    },
  ],
  iceTransportPolicy: 'all',
  iceCandidatePoolSize: 10,
};

function VideoCallRoom() {
  const { sessionId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [remoteUser, setRemoteUser] = useState(null);
  const [callStatus, setCallStatus] = useState('connecting');
  const [iceConnectionState, setIceConnectionState] = useState('new');
  const [peerReady, setPeerReady] = useState(false);

  // Refs
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const peerConnection = useRef(null);
  const localStream = useRef(null);
  const socket = useRef(null);
  const reconnectAttempts = useRef(0);
  const reconnectTimer = useRef(null);
  const pingInterval = useRef(null);
  const mountedRef = useRef(false);
  const isNegotiating = useRef(false);
  const iceGatheringTimeout = useRef(null);
  const offerSent = useRef(false);
  const sessionRef = useRef(null);
  const candidateQueue = useRef([]); // candidates before remoteDescription is set
  const handleDisconnectRef = useRef(null); // Ref for handleDisconnect

  const debugLog = useCallback((message, data = null) => {
    const timestamp = new Date().toISOString();
    console.log(`[${timestamp}] ${message}`, data ?? '');
  }, []);

  const fetchSessionWithRetry = useCallback(
    async (token, retries = 3) => {
      debugLog(`Fetching session (attempt ${retries} remaining)`);
      try {
        const { data } = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/sessions/${sessionId}`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
              'Content-Type': 'application/json',
            },
            timeout: 5000,
          }
        );
        return data;
      } catch (err) {
        if (retries <= 1) throw err;
        await new Promise((r) => setTimeout(r, 1000 * (4 - retries)));
        return fetchSessionWithRetry(token, retries - 1);
      }
    },
    [sessionId, debugLog]
  );

  const setupMediaDevices = useCallback(async () => {
    debugLog('Setting up media devices');
    try {
      const constraints = {
        video: {
          width: { ideal: 1280 },
          height: { ideal: 720 },
          frameRate: { ideal: 30 },
        },
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);

      if (!mountedRef.current) {
        stream.getTracks().forEach((t) => t.stop());
        return;
      }

      localStream.current = stream;
      if (localVideoRef.current) {
        localVideoRef.current.srcObject = stream;
        debugLog('Local stream assigned to video element', { tracks: stream.getTracks() });
        try {
          await localVideoRef.current.play();
          debugLog('Local video playback started');
        } catch (e) {
          debugLog('Local video play error', e);
        }
      } else {
        debugLog('Error: localVideoRef is null');
      }
      return stream;
    } catch (err) {
      debugLog('Media device error', err);
      throw new Error('Could not access camera/microphone. Please check permissions.');
    }
  }, [debugLog]);

  const flushQueuedCandidates = useCallback(async () => {
    const pc = peerConnection.current;
    if (!pc) return;
    if (!pc.remoteDescription) return;

    const queue = candidateQueue.current;
    while (queue.length) {
      const c = queue.shift();
      try {
        await pc.addIceCandidate(c);
      } catch (e) {
        debugLog('Error flushing queued ICE candidate', e);
      }
    }
  }, [debugLog]);

  const createAndSendOffer = useCallback(async () => {
    if (isNegotiating.current) return;
    isNegotiating.current = true;

    try {
      const pc = peerConnection.current;
      if (!pc) throw new Error('PC not ready for offer');

      debugLog('Creating offer');
      const offer = await pc.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true,
      });
      await pc.setLocalDescription(offer);

      // Wait for ICE gathering to complete or timeout
      if (pc.iceGatheringState !== 'complete') {
        await new Promise((resolve) => {
          const checkState = () => {
            if (pc.iceGatheringState === 'complete') {
              clearTimeout(iceGatheringTimeout.current);
              pc.removeEventListener('icegatheringstatechange', checkState);
              resolve();
            }
          };
          pc.addEventListener('icegatheringstatechange', checkState);
          iceGatheringTimeout.current = setTimeout(() => {
            pc.removeEventListener('icegatheringstatechange', checkState);
            debugLog('ICE gathering timeout, proceeding anyway');
            resolve();
          }, ICE_GATHERING_TIMEOUT);
        });
      }

      if (socket.current?.readyState === WebSocket.OPEN) {
        debugLog('Sending offer');
        socket.current.send(JSON.stringify({ type: 'offer', offer: pc.localDescription }));
        offerSent.current = true;
      }
    } catch (err) {
      debugLog('Offer creation error', err);
      setError('Failed to establish connection. Please try again.');
    } finally {
      isNegotiating.current = false;
    }
  }, [debugLog]);

  const handleOffer = useCallback(
    async (offer) => {
      try {
        debugLog('Received offer');
        const pc = peerConnection.current;
        if (!pc) throw new Error('PC not ready for offer');

        await pc.setRemoteDescription(new RTCSessionDescription(offer));
        await flushQueuedCandidates();

        debugLog('Creating answer');
        const answer = await pc.createAnswer();
        await pc.setLocalDescription(answer);

        if (socket.current?.readyState === WebSocket.OPEN) {
          debugLog('Sending answer');
          socket.current.send(JSON.stringify({ type: 'answer', answer }));
        }
      } catch (err) {
        debugLog('Error handling offer', err);
        setError('Failed to establish connection. Please try again.');
      }
    },
    [flushQueuedCandidates, debugLog]
  );

  const handleAnswer = useCallback(
    async (answer) => {
      try {
        debugLog('Received answer');
        const pc = peerConnection.current;
        if (!pc) throw new Error('PC not ready for answer');

        await pc.setRemoteDescription(new RTCSessionDescription(answer));
        await flushQueuedCandidates();
      } catch (err) {
        debugLog('Error handling answer', err);
        setError('Failed to establish connection. Please try again.');
      }
    },
    [flushQueuedCandidates, debugLog]
  );

  const cleanupResources = useCallback(() => {
    debugLog('Cleaning up resources');
    clearTimeout(reconnectTimer.current);
    clearTimeout(iceGatheringTimeout.current);
    clearInterval(pingInterval.current);

    if (socket.current) {
      try { socket.current.close(); } catch {}
      socket.current = null;
    }
    if (peerConnection.current) {
      try { peerConnection.current.close(); } catch {}
      peerConnection.current = null;
    }
    if (localStream.current) {
      try { localStream.current.getTracks().forEach((t) => t.stop()); } catch {}
      localStream.current = null;
    }
    candidateQueue.current = [];
    offerSent.current = false;
    isNegotiating.current = false;
  }, [debugLog]);

  const handleEndCall = useCallback(async () => {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) return;

      const token = await user.getIdToken();
      await axios.post(
        `${process.env.REACT_APP_API_BASE_URL}/sessions/${sessionId}/end`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      cleanupResources();
      navigate('/dashboard');
    } catch (err) {
      debugLog('Error ending call', err);
      setError('Failed to properly end call. Please try again.');
    }
  }, [sessionId, navigate, cleanupResources, debugLog]);

  const setupSignalingSocket = useCallback(
    (sessionId, token) => {
      debugLog('Initializing WebSocket connection');
      if (socket.current) socket.current.close();

      const wsUrl = `wss://api.teachmego.com/ws/signaling/${sessionId}?token=${token}`;
      socket.current = new WebSocket(wsUrl);

      pingInterval.current = setInterval(() => {
        if (socket.current?.readyState === WebSocket.OPEN) {
          socket.current.send(JSON.stringify({ type: 'ping' }));
          debugLog('Sent ping to server');
        }
      }, PING_INTERVAL);

      socket.current.onopen = () => {
        debugLog('WebSocket connected');
        reconnectAttempts.current = 0;
        setCallStatus('connected');
        socket.current.send(JSON.stringify({ type: 'ready' }));
      };

      socket.current.onclose = (event) => {
        debugLog('WebSocket closed', event);
        clearInterval(pingInterval.current);
        if (mountedRef.current && !event.wasClean) {
          // Use the ref instead of direct function call
          if (handleDisconnectRef.current) {
            handleDisconnectRef.current(event);
          }
        }
      };

      socket.current.onerror = (error) => {
        debugLog('WebSocket error', error);
      };

      socket.current.onmessage = async (event) => {
        try {
          const message = JSON.parse(event.data);
          debugLog('WebSocket message received', message);

          if (message.type === 'pong') return;
          if (!peerConnection.current) {
            debugLog('Peer connection not ready - queuing/ignoring message');
            return;
          }

          switch (message.type) {
            case 'offer':
              await handleOffer(message.offer);
              break;
            case 'answer':
              await handleAnswer(message.answer);
              break;
            case 'peer-ready':
              debugLog('Peer is ready!');
              setPeerReady(true);
              if (sessionRef.current?.isInitiator && !offerSent.current) {
                await createAndSendOffer();
              }
              break;
            case 'ice-candidate': {
              if (!message.candidate) break;
              const pc = peerConnection.current;
              const ice = new RTCIceCandidate(message.candidate);
              if (pc.remoteDescription) {
                try {
                  await pc.addIceCandidate(ice);
                } catch (err) {
                  debugLog('Error adding ICE candidate', err);
                }
              } else {
                candidateQueue.current.push(ice);
              }
              break;
            }
            default:
              debugLog('Unknown message type', message.type);
          }
        } catch (err) {
          debugLog('Message processing error', err);
        }
      };
    },
    [createAndSendOffer, handleAnswer, handleOffer, debugLog]
  );

  // Now define handleDisconnect after setupSignalingSocket
  const handleDisconnect = useCallback(
    (event) => {
      if (!mountedRef.current) return;
      debugLog('Disconnect event:', event);
      setPeerReady(false);

      if (reconnectAttempts.current >= MAX_RECONNECT_ATTEMPTS) {
        setCallStatus('disconnected');
        setError('Connection lost. Please try again later.');
        return;
      }

      const delay = BASE_RECONNECT_DELAY * Math.pow(2, reconnectAttempts.current);
      reconnectAttempts.current += 1;
      setCallStatus(`reconnecting (${reconnectAttempts.current}/${MAX_RECONNECT_ATTEMPTS})`);

      reconnectTimer.current = setTimeout(async () => {
        if (!mountedRef.current) return;
        const auth = getAuth();
        const user = auth.currentUser;
        if (user) {
          try {
            const token = await user.getIdToken(true);
            setupSignalingSocket(sessionId, token);
          } catch (err) {
            debugLog('Reconnect failed', err);
            handleDisconnect();
          }
        }
      }, delay);
    },
    [sessionId, setupSignalingSocket, debugLog]
  );

  // Update the ref when handleDisconnect is defined
  useEffect(() => {
    handleDisconnectRef.current = handleDisconnect;
  }, [handleDisconnect]);

  const createPeerConnection = useCallback(() => {
    debugLog('Creating new peer connection');
    if (peerConnection.current) peerConnection.current.close();
    const pc = new RTCPeerConnection(rtcConfig);
    peerConnection.current = pc;

    if (localStream.current) {
      localStream.current.getTracks().forEach((track) => {
        pc.addTrack(track, localStream.current);
        debugLog('Added track', { kind: track.kind, id: track.id });
      });
    }

    pc.ontrack = (event) => {
      debugLog('Received remote track', { streams: event.streams, tracks: event.streams[0]?.getTracks() });
      if (remoteVideoRef.current && event.streams && event.streams[0]) {
        remoteVideoRef.current.srcObject = event.streams[0];
        remoteVideoRef.current.play().catch((e) => debugLog('Remote video play error', e));
        setCallStatus('active');
      } else {
        debugLog('Remote video ref or stream missing', { ref: !!remoteVideoRef.current, streams: event.streams });
      }
    };

    pc.onicecandidate = ({ candidate }) => {
      if (candidate && socket.current?.readyState === WebSocket.OPEN) {
        debugLog('Sending ICE candidate', candidate);
        socket.current.send(JSON.stringify({ type: 'ice-candidate', candidate }));
      }
    };

    pc.oniceconnectionstatechange = () => {
      const state = pc.iceConnectionState;
      debugLog('ICE connection state:', state);
      setIceConnectionState(state);
      if (state === 'connected') setCallStatus('active');
      else if (state === 'disconnected' || state === 'failed') handleDisconnect();
    };

    pc.onicegatheringstatechange = () => {
      debugLog('ICE gathering state:', pc.iceGatheringState);
    };

    pc.onsignalingstatechange = () => {
      debugLog('Signaling state:', pc.signalingState);
    };

    pc.onnegotiationneeded = async () => {
      debugLog('Negotiation needed');
      if (!sessionRef.current?.isInitiator || offerSent.current) return;
      await createAndSendOffer();
    };

    return pc;
  }, [debugLog, createAndSendOffer, handleDisconnect]);

  useEffect(() => {
    mountedRef.current = true;
    debugLog('Component mounted');

    const initializeCall = async () => {
      try {
        const auth = getAuth();
        const user = auth.currentUser;
        if (!user) throw new Error('User not authenticated');

        const token = await user.getIdToken(false);
        const session = await fetchSessionWithRetry(token);
        sessionRef.current = session;

        debugLog('Fetched session:', session);

        const userUid = user.uid;
        const isInitiator = userUid === session.initiatorId;
        sessionRef.current.isInitiator = isInitiator;

        debugLog('User UID:', userUid);
        debugLog('Session initiatorId:', session.initiatorId);
        debugLog('isInitiator:', isInitiator);

        const tutorData = session.tutor || { id: session.tutorId, username: 'Tutor' };
        const studentData = session.student || { id: session.studentId, username: 'Student' };

        const viewingAsTutor = userUid === tutorData.id;
        const remoteUserData = viewingAsTutor ? studentData : tutorData;
        const remoteRole = viewingAsTutor ? 'Student' : 'Tutor';

        if (!mountedRef.current) return;
        setRemoteUser({ name: remoteUserData.username, role: remoteRole, id: remoteUserData.id });

        const startDelay = (session.startsAt ?? Date.now()) - Date.now();
        if (startDelay > 0) {
          setCallStatus('waiting');
          await new Promise((resolve) => setTimeout(resolve, startDelay));
          if (!mountedRef.current) return;
        }

        await setupMediaDevices();
        createPeerConnection();
        setupSignalingSocket(sessionId, token);
      } catch (err) {
        debugLog('Initialization failed', err);
        if (mountedRef.current) {
          setError(err.response?.data?.error || err.message || 'Failed to initialize call');
          setCallStatus('failed');
        }
      } finally {
        if (mountedRef.current) setLoading(false);
      }
    };

    initializeCall();

    return () => {
      mountedRef.current = false;
      cleanupResources();
    };
  }, [
    fetchSessionWithRetry,
    setupMediaDevices,
    createPeerConnection,
    setupSignalingSocket,
    cleanupResources,
    sessionId,
    debugLog,
  ]);

  useEffect(() => {
    debugLog('Render state', { callStatus, error, iceConnectionState });
  }, [callStatus, error, iceConnectionState, debugLog]);

  if (loading) {
    return (
      <div className="video-call-loading">
        <div className="loading-spinner"></div>
        <p>Setting up your call...</p>
        <p>Status: {callStatus}</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="video-call-error">
        <h3>Error</h3>
        <p>{error}</p>
        <button onClick={() => window.location.reload()}>Retry</button>
        <button onClick={() => navigate('/dashboard')}>Dashboard</button>
      </div>
    );
  }

  return (
    <div className="video-call-room">
      <div className="status-bar">
        <span className={`status-indicator ${callStatus}`}>
          {callStatus} {iceConnectionState && `(${iceConnectionState})`}
        </span>
      </div>

      <div className="video-container">
        <div className="remote-video-wrapper">
          <video ref={remoteVideoRef} autoPlay playsInline />
          {remoteUser && (
            <div className="user-info">
              <h3>{remoteUser.name}</h3>
              <p>{remoteUser.role}</p>
            </div>
          )}
        </div>
        <div className="local-video-wrapper">
          <video ref={localVideoRef} autoPlay playsInline muted />
        </div>
      </div>

      <div className="peer-status">
        <span className={`status-dot ${peerReady ? 'online' : 'offline'}`}></span>
        {peerReady ? 'Peer connected' : 'Waiting for peer...'}
      </div>

      <div className="controls">
        <button onClick={handleEndCall} className="end-call-button">
          End Call
        </button>
      </div>
    </div>
  );
}

export default VideoCallRoom;
